import { format } from 'date-fns';
import { useApplicationContext } from '../providers/ApplicationProvider';
import { attachmentLabel, fileIcon } from '../helpers/utils';
import { extractFileFields, extractRowFileFields } from '../helpers/fileFields';
import { rowFileFieldNames } from '../helpers/applicationFormData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AttachmentType, FileType } from '../helpers/types';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../providers/AuthProvider';

const ApplicationAttachments = () => {
    const { application, applicationConfig, loadApplication } = useApplicationContext();
    const { apiCall } = useAuthContext();
    const { t } = useTranslation();

    const labelOrder = ['filer_bilag', 'filer_prosjektbeskrivelse', 'filer_regnskap', 'filer_prosjektbudsjett', 'filer_finansieringsplan', 'filer_fremdriftsplan', 'filer_andre1', 'filer_andre2'];

    // Fil-feltene fra søknadsmalen (fileuploadsingle/fileupload). Foretrekk den
    // eksakte versjonerte malen; fall tilbake til gjeldende mal fra ConfigProvider
    // hvis den eksakte mangler fil-felt (eller ikke er lagt på).
    const exactFields = extractFileFields(application?.applicationTemplate);
    const templateFields = exactFields.length ? exactFields : extractFileFields(applicationConfig);
    // Radbilag (utleggstabeller) er distinkte filer, én per rad — ikke versjoner
    // av én fil — og vises derfor hver for seg, under tabellens navn.
    const rowFields = [
        ...extractRowFileFields(application?.applicationTemplate),
        ...extractRowFileFields(applicationConfig),
    ];
    const rowFieldNames = rowFileFieldNames(application, applicationConfig);
    const multipleFields = new Set([
        ...templateFields.filter((f) => f.multiple).map((f) => f.name),
        ...rowFieldNames,
    ]);

    // Opplastings-nedtrekket speiler malens fil-felt, med «Bilag» og «Annet»
    // alltid tilgjengelig (som i admin).
    const fileTypes = templateFields.map((f) => ({ key: f.name, label: f.label }));
    if (!fileTypes.some((x) => x.key === 'filer_bilag')) {
        fileTypes.push({ key: 'filer_bilag', label: t('files_voucher') });
    }
    if (!fileTypes.some((x) => x.key === 'filer_andre1')) {
        fileTypes.push({ key: 'filer_andre1', label: t('files_other_1') });
    }

    // Visningsnavn: malens element-label vinner, ellers kjent oversettelse.
    const resolveLabel = (type: string) =>
        templateFields.find((f) => f.name === type)?.label
        ?? rowFields.find((f) => f.name === type)?.label
        ?? attachmentLabel(type, t);

    const uploadFile = async (key: string) => {
        // Create an input element dynamically
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.docx,.doc,.xlsx,.xlsm,.csv,.xls,.ppt,.pptx,.pdf';
        fileInput.style.display = 'none';

        // Add event listener to handle file selection
        fileInput.addEventListener('change', async (event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files || target.files.length === 0) return;
            const selectedFile = target.files[0];

            try {
                // API call to upload the file
                const response = await apiCall({
                    action: 'uploadFile',
                    [key]: selectedFile,
                    application_id: application?.id,
                });

                if (response.data.status === 200) {
                    loadApplication();
                }
            } catch (error) {
                console.error('Error uploading file:', error);
            }
        });

        // Trigger the file input click
        fileInput.click();
    };

    const Attachment = ({ type, files, index }: AttachmentType) => {
        const sortedFiles = [...files].sort((a: FileType, b: FileType) => {
            const aHasDate = !!a.createdon;
            const bHasDate = !!b.createdon;

            // Files without date go last
            if (!aHasDate && bHasDate) return 1;
            if (aHasDate && !bHasDate) return -1;

            // Both missing date → keep original relative order
            if (!aHasDate && !bHasDate) return 0;

            // Both have date → newest first
            return new Date(b.createdon!).getTime() - new Date(a.createdon!).getTime();
        });

        return (
            <div className={`accordion-item ${sortedFiles.length < 2 && 'accordion-without-file-list'}`}>
                <h2 className="accordion-header fs-normal">
                    <div className="accordion-button px-0 d-block fw-normal collapsed" data-bs-toggle="collapse" data-bs-target={`#collapse-x-${index}`} aria-expanded="false" aria-controls={`collapse-x-${index}`}>
                        <div className="row">
                            <div className="col-12 col-lg-2">{resolveLabel(type)}</div>
                            <a className="col-10 col-lg-5 text-primary text-decoration-none mt-3 mb-2 my-lg-0" href={import.meta.env.VITE_API_BASE_URL + sortedFiles[0].download_url}>
                                <FontAwesomeIcon icon={fileIcon(sortedFiles[0].name?.split('.').pop() ?? '')} className="me-2" />
                                <span>{sortedFiles[0].name}</span>
                            </a>
                            {sortedFiles[0].createdon && <div className="col-4 col-lg-2">{format(sortedFiles[0].createdon, 'dd.MM.yyyy')}</div>}
                            <div className="col-8 col-lg-2">{sortedFiles[0].createdby_name}</div>
                        </div>
                    </div>
                </h2>

                {/* If it has more files */}
                {sortedFiles.length >= 2 && (
                    <div id={`collapse-x-${index}`} className="accordion-collapse collapse" data-bs-parent="#accordionExample">
                        <div className="accordion-body px-0 py-0">
                            {sortedFiles.slice(1).map((file: FileType, i) => (
                                <div key={i} className="row pb-3">
                                    <a className="col-12 offset-lg-2 col-lg-5 text-primary text-decoration-none mb-2 mb-lg-0" href={import.meta.env.VITE_API_BASE_URL + file.download_url}>
                                        <FontAwesomeIcon icon={fileIcon(file.name?.split('.').pop() ?? '')} className="me-2" />
                                        <span>{file.name}</span>
                                    </a>
                                    <div className="col-4 col-lg-2">{file.createdon ? format(file.createdon, 'dd.MM.yyyy') : ''}</div>
                                    <div className="col-8 col-lg-2">{file.createdby_name}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="row mb-3 fw-medium application-files-table-header">
                <div className="col-12 col-lg-2">
                    <h6>{t('files_type')}</h6>
                </div>
                <div className="col-12 col-lg-5">
                    <h6>{t('files_filename')}</h6>
                </div>
                <div className="col-4 col-lg-2">
                    <h6>{t('files_uploaded')}</h6>
                </div>
                <div className="col-8 col-lg-2">
                    <h6>{t('files_uploaded_by')}</h6>
                </div>
            </div>

            <div className="accordion" id="accordionTableFiles">
                {application?.attachments &&
                    Object.entries(application.attachments)
                        .sort(([typeA], [typeB]) => {
                            const indexA = labelOrder.indexOf(typeA);
                            const indexB = labelOrder.indexOf(typeB);

                            // If type is not found in labelOrder, push it to the bottom
                            return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                        })
                        // Multiple-opplasting (fileupload): hver fil vises på egen rad.
                        // Versjonert enkeltfil / ukjent: samlet (nyeste + versjoner i trekkspill).
                        .flatMap(([type, files]) => {
                            const list = files as FileType[];
                            if (multipleFields.has(type)) {
                                return list.map((file) => ({ type, files: [file] }));
                            }
                            return [{ type, files: list }];
                        })
                        .map((row, index) => (
                            <Attachment
                                key={`${row.type}-${row.files[0]?.location ?? index}`}
                                type={row.type}
                                files={row.files}
                                index={index}
                            />
                        ))}
            </div>

            <div className="row mt-4">
                <div className="col position-relative">
                    <div className="dropdown">
                        <button className="btn btn-outline-primary btn-lg rounded-pill" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <FontAwesomeIcon icon={['fal', 'upload']} className="me-2" />
                            {t('upload_file')}
                        </button>

                        <ul className="dropdown-menu">
                            {fileTypes.map(({ key, label }) => (
                                <li key={key}>
                                    <a className="dropdown-item" href="#" onClick={() => uploadFile(key)}>
                                        {label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ApplicationAttachments;
